import Vue from 'vue';
import Vuex from 'vuex';

import axios from 'axios';
import createPersistedState from 'vuex-persistedstate';
import axiosInstance from './repositories/Repository';

const vue = Vue.use(Vuex);

const LOGIN_URL = `${process.env.VUE_APP_API_URL}/authenticate`;

export default new Vuex.Store({
  state: {
    content: {
      padding: '50px',
      sidebarExpanded: true,
    },
    instanceSettings: {
      memberEditEnabled: false,
      sidepanels: {
        top: {
          activeTab: 'memberlist',
        },
        bottom: {
          activeTab: 'memberlist',
        },
      },
      chapterEntryMode: 'default',
      selectedChapterId: null,
      selectedEntryId: null,
    },

    authenticated: false,
    authToken: null,
    user: null,

    currentChannelTitle: '',
    currentChannelId: null,
    channelMemberList: null,
    channelSettings: null,

    events: null,
    instance: null,
    liveSeconds: null,
    currentVideoTimestamp: -1,

    shouldLoadVideoPlayer: false,
    videoPlayerUrl: null,

    deviceControlList: null,

    currentLocale: null,

    liveStreamOnline: false,

  },
  getters: {
    // Get Locale
    getLocale(state) {
      return state.currentLocale;
    },

    // Channel settings
    getChannelSettings(state) {
      return state.channelSettings;
    },

    getActiveChannelName(state) {
      return state.currentChannelTitle;
    },

    // Timestamp of current Video
    getVideoTimestamp(state) {
      return state.currentVideoTimestamp;
    },

    // Should load video player
    getShouldLoadVideoPlayer(state) {
      return state.shouldLoadVideoPlayer;
    },

    // video player url
    getVideoPlayerUrl(state) {
      return state.videoPlayerUrl;
    },

    // Events
    events(state) {
      return state.events;
    },

    // Instance Live
    getInstanceLiveElapsedSeconds(state) {
      if (state.liveSeconds === null) {
        return null;
      }

      return state.liveSeconds;
      // vue.$moment().startOf('day').seconds(state.liveSeconds).format('HH:mm:ss')
    },

    // Layout
    getContentPadding(state) {
      // In Read Only there is no sidebar so set padding to 0.
      if (state.instance !== null) {
        if (state.instance.isReadOnly) {
          return '0';
        }
      }

      return state.content.padding;
    },
    isSidebarExpanded(state) {
      return state.content.sidebarExpanded;
    },

    // Instance
    instanceGroupEditEnabled(state) {
      return state.instanceSettings.memberEditEnabled;
    },

    instanceInLiveMode(state, getters) {
      if (!getters.instanceLoaded) {
        return false;
      }

      return state.instance.isLive;
    },
    instanceLiveData(state, getters) {
      if (!getters.instanceInLiveMode) {
        return false;
      }

      return getters.activeInstanceData.live;
    },
    instanceInReadOnly(state, getters) {
      if (getters.activeInstanceData !== null) {
        return getters.activeInstanceData.isReadOnly || false;
      }

      return false;
    },
    instanceInPostProcessingMode(state, getters) {
      return !getters.instanceInLiveMode;
    },
    instanceLoaded(state) {
      return state.instance !== null;
    },
    activeInstanceData(state) {
      return state.instance;
    },
    getSidePanelTopTabSettings(state, getters) {
      return state.instanceSettings.sidepanels.top;
    },
    getSidePanelBottomTabSettings(state, getters) {
      return state.instanceSettings.sidepanels.bottom;
    },
    chapterEntryMode(state) {
      return state.instanceSettings.chapterEntryMode;
    },


    // Channel
    channelSelected(state) {
      return state.currentChannelId !== null;
    },
    activeChannelMemberList(state) {
      return state.channelMemberList;
    },

    // User
    getBearer(state, getters) {
      if (getters.isLoggedIn && state.authToken !== null) {
        return `Bearer ${state.authToken}`;
      }
      return null;
    },
    isLoggedIn(state, getters) {
      return state.user !== null;
    },
    isAdmin(state, getters) {
      return getters.isLoggedIn && state.user.isAdmin === true;
    },
    // Chapters
    getActiveInstanceChapters(state, getters) {
      if (!getters.instanceLoaded) return null;

      return state.instance.chapters;
    },

    // selected Chapter + Entry
    getSelectedChapterId(state) {
      return state.instanceSettings.selectedChapterId;
    },
    getSelectedEntryId(state) {
      return state.instanceSettings.selectedEntryId;
    },
    isChapterSelected(state) {
      return state.instanceSettings.selectedChapterId !== null && state.instanceSettings.selectedEntryId === null;
    },
    isEntrySelected(state) {
      return state.instanceSettings.selectedEntryId !== null;
    },
  },
  mutations: {
    // Locale
    setLocale(state, locale) {
      state.currentLocale = locale;
    },

    // Channel settings
    setChannelSettings(state, data) {
      state.channelSettings = data;
    },


    // Timestamp of current Video
    setVideoTimestamp(state, timestamp) {
      state.currentVideoTimestamp = timestamp;
    },

    // Should load video player
    setShouldLoadVideoPlayer(state, data) {
      state.shouldLoadVideoPlayer = data;
    },

    // video player url
    setVideoPlayerUrl(state, url) {
      state.videoPlayerUrl = url;
    },

    // Events
    setEvents(state, data) {
      state.events = data;
    },

    // User
    setAuthToken(state, data) {
      state.authToken = data.token;
      state.authenticated = true;
      state.user = data.user;
      state.currentChannelId = data.user.defaultChannel;
      state.currentChannelTitle = data.user.defaultChannelName;

      axiosInstance.defaults.headers.common.Authorization = `Bearer ${data.token}`;
    },
    logout(state) {
      state.authToken = '';
      state.authenticated = false;
      state.user = null;
      state.currentChannelId = null;
      state.currentChannelTitle = '';
      state.channel = null;
      state.instance = null;
      state.mode = null;
      state.content.padding = '50px';
      state.content.sidebarExpanded = true;
      state.liveSeconds = null;
      state.instanceSettings.chapterEntryMode = 'default';
      state.deviceControlList = null;
      state.channelMemberList = null;
      state.channelSettings = null;
      state.liveStreamOnline = false;
      state.instanceSettings.sidepanels = {
        top: {
          activeTab: 'memberlist',
        },
        bottom: {
          activeTab: 'memberlist',
        },
      };

      axiosInstance.defaults.headers.common.Authorization = null;
    },

    // Channel
    setSelectedChannel(state, data) {
      state.currentChannelTitle = data.title;
      state.currentChannelId = data.id;
      state.events = null;
    },
    setChannelMembers(state, memberlist) {
      state.channelMemberList = memberlist;
    },

    // Instance
    setInstanceData(state, data) {
      state.instance = data;
      state.liveSeconds = null;

      // Disable Edit Mode when Instance closed
      if (data === null) {
        state.instanceSettings.memberEditEnabled = false;
        state.instanceSettings.sidepanels.top.activeTab = null;
        state.instanceSettings.sidepanels.bottom.activeTab = null;
        state.instanceSettings.selectedChapterId = null;
        state.instanceSettings.selectedEntryId = null;
        state.instanceSettings.chapterEntryMode = 'default';
        state.currentVideoTimestamp = -1;
        state.liveStreamOnline = false;
      }
    },
    toggleGroupEditMode(state) {
      state.instanceSettings.memberEditEnabled = !state.instanceSettings.memberEditEnabled;
    },
    setInstanceMembers(state, data) {
      state.instance.members = data;
    },

    setLiveStreamOnlineStatus(state, status) {
      state.liveStreamOnline = status;
    },
    /**
     * Sets the active Tab.
     * data.position -> 'top', 'bottom', 'both'
     * data.tab
     *
     * @param state
     * @param data
     * @returns {boolean}
     */
    setActiveSidePanelTab(state, data) {
      if (data.position === 'top') {
        state.instanceSettings.sidepanels.top.activeTab = data.tab;
      } else if (data.position === 'bottom') {
        state.instanceSettings.sidepanels.bottom.activeTab = data.tab;
      } else if (data.position === 'both') {
        state.instanceSettings.sidepanels.top.activeTab = data.tab;
        state.instanceSettings.sidepanels.bottom.activeTab = data.tab;
      } else {
        return false;
      }
    },
    /**
     * Sets the data.position tab to data.setTo if it currently equals data.equals
     * Param data: {data.position, data.equals, data.setTo}
     * @param state
     * @param data
     */
    setActiveSidePanelTabIfCurrentTabEquals(state, data) {
      const activeTab = data.position === 'top' ? state.instanceSettings.sidepanels.top.activeTab : state.instanceSettings.sidepanels.bottom.activeTab;

      // The current tab matches data.equals
      if (activeTab === data.equals) {
        if (data.position === 'top') {
          state.instanceSettings.sidepanels.top.activeTab = data.setTo;
        } else if (data.position === 'bottom') {
          state.instanceSettings.sidepanels.bottom.activeTab = data.setTo;
        } else if (data.position === 'both') {
          state.instanceSettings.sidepanels.top.activeTab = data.setTo;
          state.instanceSettings.sidepanels.bottom.activeTab = data.setTo;
        } else {
          return false;
        }
      }
    },
    sidePanelVideoLiveStreamToggle(state) {
      state.instanceSettings.sidepanels.top.activeTab = 'memberlist';
      state.instanceSettings.sidepanels.bottom.activeTab = 'memberlist';
    },
    setChapterEntryMode(state, mode) {
      /*
        Modes
        --------
        Default: Selecting Entries / Chapter, Creating, Deleting etc
        Move: Move Chapter/entries
        Timeshift: Timeshift-Mode
       */
      if (mode !== 'default' && mode !== 'move' && mode !== 'timeshift') {
        return false;
      }

      state.instanceSettings.chapterEntryMode = mode;
    },

    // Layout
    expandSidebar(state, bool) {
      state.content.sidebarExpanded = bool;
      state.content.padding = bool ? '50px' : '350px';
    },

    // live
    updateInstanceLiveSeconds(state, seconds) {
      state.liveSeconds = seconds;
    },

    // Selected Chapter + Entry
    setSelectedChapterId(state, id) {
      state.instanceSettings.selectedChapterId = id;
    },
    setSelectedEntryId(state, id) {
      state.instanceSettings.selectedEntryId = id;
    },

    setDeviceControlList(state, devices) {
      state.deviceControlList = devices;
    },
  },
  actions: {
    // Device Control list
    setDeviceControlList({ commit }, devices) {
      commit('setDeviceControlList', devices);
    },

    // Events
    setEvents({ commit }, events) {
      commit('setEvents', events);
    },

    setInstanceMembers({ commit }, memberlist) {
      commit('setInstanceMembers', memberlist);
    },

    setChannelMembers({ commit }, memberlist) {
      commit('setChannelMembers', memberlist);
    },

    expandSidebar({ commit }, bool) {
      commit('expandSidebar', bool);
    },

    toggleGroupEditMode({ commit }) {
      commit('toggleGroupEditMode');
    },

    setSelectedChannel({ commit }, payload) {
      commit('setSelectedChannel', payload);
      commit('setInstanceData', null);
    },
    setInstanceData({ commit }, payload) {
      commit('setInstanceData', payload);
    },
    logout({ commit }) {
      commit('logout');
    },
    login({ commit }, payload) {
      return new Promise((resolve, reject) => {
        axios.post(LOGIN_URL, {
          username: payload.username,
          password: payload.password,
        })
          .then(((response) => {
            commit('setAuthToken', response.data);
            resolve(response);
          }))
          .catch((error) => {
            reject(error);
          });
      });
    },

    // Live Mode
    updateInstanceLiveSeconds({ commit }, seconds) {
      commit('updateInstanceLiveSeconds', seconds);
    },
  },
  plugins: [createPersistedState()],
});
